import type { FontOptions } from 'text-to-svg';

import { type EdgeData, isRenderableEdgeData } from '../../edges/EdgeData';
import { getEdgeBoxId } from '../../edges/getEdgeBoxId';
import { getEdgePositionsBounds } from '../../edges/getEdgePositionsBounds';
import { type Bounds, bounds } from '../../elements/bounds';
import { type Rectangle, rectangle } from '../../elements/rectangle';
import { type BoxId, type NodeData, getNodeDataBounds } from '../../nodes';
import {
  NODE_BOX_BORDER_RADIUS,
  NODE_BOX_LABEL_FONT_SIZE,
  NODE_BOX_LABEL_PADDING,
  NODE_BOX_LINE_COLOR,
  NODE_BOX_LINE_WIDTH,
  getNodeBoxPositions,
} from '../../nodes/boxes';
import { getRoundedRectanglePath } from '../../nodes/icons/common';
import { getGraphFonts } from '../../svg/text';
import { makeTag } from '../../svg/utils';
import type { NonEmptyArray } from '../../types/NonEmptyArray';
import { rgbNumberToString } from '../../utils/rgb';
import type { GraphExportData } from './GraphExportData';

interface Box {
  name: string;
  edges: EdgeData[];
  nodes: NodeData[];
}

const getBoxes = (graph: GraphExportData): Box[] => {
  const boxesByName = new Map<BoxId, Box>();
  const getOrCreateBox = (id: BoxId): Box => {
    if (!boxesByName.has(id)) {
      boxesByName.set(id, {
        name: graph.boxLabels[id] ?? id,
        edges: [],
        nodes: [],
      });
    }
    return boxesByName.get(id)!;
  };
  for (const node of Object.values(graph.nodes)) {
    if (node.boxId === null) {
      continue;
    }
    getOrCreateBox(node.boxId).nodes.push(node);
  }
  for (const edge of Object.values(graph.edges)) {
    const boxId = getEdgeBoxId(edge, graph.nodes);
    if (boxId === null) {
      continue;
    }
    getOrCreateBox(boxId).edges.push(edge);
  }
  return Array.from(boxesByName.values());
};

const renderBox = (box: Box): [svg: string, boundingBox: Rectangle] => {
  const entitiesBounds = bounds.merge(
    ...(box.nodes.map(getNodeDataBounds) as NonEmptyArray<Bounds>), // every found box has at least one node
    ...box.edges
      .filter(isRenderableEdgeData)
      .map((edge) => getEdgePositionsBounds(edge.positions)),
  );
  const labelFont = getGraphFonts().boxLabels;
  const fontOptions: FontOptions = {
    fontSize: NODE_BOX_LABEL_FONT_SIZE,
  };
  const labelTextMetrics = labelFont.getMetrics(box.name, fontOptions);
  const boxPositions = getNodeBoxPositions(entitiesBounds, labelTextMetrics);
  const boxSvg = makeTag('path', {
    d: getRoundedRectanglePath(
      boxPositions.left,
      boxPositions.top,
      boxPositions.width,
      boxPositions.height,
      NODE_BOX_BORDER_RADIUS,
    ),
    fill: 'none',
    stroke: rgbNumberToString(NODE_BOX_LINE_COLOR),
    'stroke-width': NODE_BOX_LINE_WIDTH,
  });
  const labelTextSvg = labelFont.getPath(box.name, {
    ...fontOptions,
    anchor: 'center middle',
    x: boxPositions.centerX,
    y: boxPositions.top,
  });
  const labelBackgroundSvg = makeTag('rect', {
    x:
      boxPositions.centerX -
      labelTextMetrics.width / 2 -
      NODE_BOX_LABEL_PADDING,
    y: boxPositions.top - labelTextMetrics.height / 2,
    width: labelTextMetrics.width + NODE_BOX_LABEL_PADDING * 2,
    height: labelTextMetrics.height,
    fill: 'white',
  });
  return [
    `${boxSvg}${labelBackgroundSvg}${labelTextSvg}`,
    bounds.toRectangle(boxPositions.bounds),
  ];
};

export const renderBoxes = (
  graph: GraphExportData,
): [svg: string, boundingBox: Rectangle | null] => {
  const renderedBoxes = getBoxes(graph).map(renderBox);

  return [
    renderedBoxes.map(([svg]) => svg).join(''),
    renderedBoxes.length > 0
      ? rectangle.mergeBoundingBoxes(
          ...renderedBoxes.map(([, boundingBox]) => boundingBox),
        )
      : null,
  ];
};
